import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../apiConfig';
import { ClipboardDocumentIcon, ArrowTopRightOnSquareIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

const TransactionsPage = ({ trades }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [transactionFilter, setTransactionFilter] = useState('All');
  const [solPrice, setSolPrice] = useState(null);
  const [loadingSolPrice, setLoadingSolPrice] = useState(true);

  // Fetch SOL price from CoinGecko
  const fetchSolPrice = async () => {
    try {
      setLoadingSolPrice(true);
      const response = await axios.get('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
      if (response.data.solana && response.data.solana.usd) {
        setSolPrice(response.data.solana.usd);
      } else {
        setSolPrice(null);
      }
    } catch (error) {
      console.error('Failed to fetch SOL price:', error);
      setSolPrice(null);
    } finally {
      setLoadingSolPrice(false);
    }
  };

  // Format USD value
  const formatUsdValue = (solAmount) => {
    if (!solAmount || !solPrice) return '$0.00';
    const usdValue = parseFloat(solAmount) * solPrice;
    return `$${usdValue.toFixed(2)}`;
  };

  // Format time to UTC+1
  const formatTimeUTC1 = (timeString) => {
    if (!timeString) return 'N/A';
    const date = new Date(timeString);
    // Add 1 hour for UTC+1
    date.setHours(date.getHours() + 1);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Generate Solscan URL for transaction
  const getSolanaExplorerUrl = (txHash) => {
    if (!txHash) return null;
    return `https://solscan.io/tx/${txHash}`;
  };

  // Open transaction in new tab
  const openTransactionLink = (txHash) => {
    const url = getSolanaExplorerUrl(txHash);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Copy address to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Address copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy: ', err);
      alert('Failed to copy address');
    }
  };

  // Generate transaction history from real trades
  const generateTransactionHistory = () => {
    const transactionList = [];

    trades.forEach((trade) => {
      // Add buy transaction
      if (trade.transaction_id && trade.buy_time) {
        transactionList.push({
          id: `buy-${trade.id || trade.token_address}`,
          type: 'buy',
          token: trade.symbol || trade.name || 'Unknown Token',
          tokenAddress: trade.token_address,
          amount: trade.original_amount || trade.amount || 0,
          timestamp: trade.buy_time,
          price: trade.buy_price,
          transactionId: trade.transaction_id,
          isMock: trade.is_mock_transaction || false,
          color: 'red',
          description: `Bought ${trade.original_amount || trade.amount || 0} SOL worth of ${trade.symbol || trade.name || 'Unknown Token'}`
        });
      }

      // Add sell transactions from partial_sales
      if (trade.partial_sales && trade.partial_sales.length > 0) {
        trade.partial_sales.forEach((sale, index) => {
          const isProfit = sale.profit_loss > 0;
          const isStopLoss = sale.reason && sale.reason.toLowerCase().includes('stop');
          
          transactionList.push({
            id: `sell-${trade.id || trade.token_address}-${index}`,
            type: isProfit ? 'profit' : (isStopLoss ? 'stop_loss' : 'sell'),
            token: trade.symbol || trade.name || 'Unknown Token',
            tokenAddress: trade.token_address,
            amount: sale.amount,
            timestamp: sale.timestamp,
            price: sale.price,
            profitLoss: sale.profit_loss,
            reason: sale.reason,
            transactionId: sale.transaction_id,
            isMock: sale.is_mock_transaction || false,
            color: isProfit ? 'green' : (isStopLoss ? 'yellow' : 'orange'),
            description: isProfit 
              ? `Took Profit from ${trade.symbol || trade.name || 'Unknown Token'}`
              : isStopLoss 
                ? `Stopped Loss of ${trade.symbol || trade.name || 'Unknown Token'}`
                : `Sold ${trade.symbol || trade.name || 'Unknown Token'}`
          });
        });
      }
      
      // Add sell transactions for completed trades without partial_sales
      else if (trade.status === 'completed' && trade.sell_time && trade.sell_transaction_id) {
        const profitLoss = trade.profit_loss || 0;
        const isProfit = profitLoss > 0;
        const isStopLoss = trade.reason && trade.reason.toLowerCase().includes('stop');
        
        transactionList.push({
          id: `sell-${trade.id || trade.token_address}`,
          type: isProfit ? 'profit' : (isStopLoss ? 'stop_loss' : 'sell'),
          token: trade.symbol || trade.name || 'Unknown Token',
          tokenAddress: trade.token_address,
          amount: trade.amount,
          timestamp: trade.sell_time,
          price: trade.sell_price,
          profitLoss: profitLoss,
          reason: trade.reason,
          transactionId: trade.sell_transaction_id,
          isMock: trade.is_mock_transaction || false,
          color: isProfit ? 'green' : (isStopLoss ? 'yellow' : 'orange'),
          description: isProfit 
            ? `Took Profit from ${trade.symbol || trade.name || 'Unknown Token'}`
            : isStopLoss 
              ? `Stopped Loss of ${trade.symbol || trade.name || 'Unknown Token'}`
              : `Sold ${trade.symbol || trade.name || 'Unknown Token'}`
        });
      }
    });

    // Sort by timestamp (newest first)
    return transactionList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  // Get color classes for transaction types
  const getTransactionColorClasses = (color) => {
    switch (color) {
      case 'red':
        return 'bg-red-900/20 border-red-500/30 text-red-400';
      case 'green':
        return 'bg-green-900/20 border-green-500/30 text-green-400';
      case 'yellow':
        return 'bg-yellow-900/20 border-yellow-500/30 text-yellow-400';
      case 'orange':
        return 'bg-orange-900/20 border-orange-500/30 text-orange-400';
      default:
        return 'bg-slate-900/20 border-slate-500/30 text-slate-400';
    }
  };

  // Get transaction icon
  const getTransactionIcon = (type) => {
    switch (type) {
      case 'buy':
        return '📈';
      case 'profit':
        return '💰';
      case 'stop_loss':
        return '🛑';
      case 'sell':
        return '📉';
      default:
        return '📊';
    }
  };

  // Filter transactions by type
  const filterTransactions = (transactions, filter) => {
    if (filter === 'All') return transactions;
    
    // Map filter names to transaction types
    const filterMap = {
      'Buy Transaction': 'buy',
      'Profit Transaction': 'profit',
      'Stop Loss Transaction': 'stop_loss',
      'Sell Transaction': 'sell'
    };
    
    const transactionType = filterMap[filter];
    if (!transactionType) return transactions;
    
    return transactions.filter(t => t.type === transactionType);
  };

  // Fetch SOL price on component mount
  useEffect(() => {
    fetchSolPrice();
    
    // Set up SOL price refresh every 30 seconds
    const solPriceInterval = setInterval(() => {
      fetchSolPrice();
    }, 30000);
    
    return () => {
      clearInterval(solPriceInterval);
    };
  }, []);

  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [transactionFilter]);

  // Generate transactions from trades
  const transactions = generateTransactionHistory();
  
  // Get filtered and paginated transactions
  const filteredTransactions = filterTransactions(transactions, transactionFilter);
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTransactions = filteredTransactions.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="space-y-6">
      {/* Real Transactions Header */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Real Transaction History</h2>
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-900/50 text-green-400">
            LIVE TRADING
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-black/40 rounded-lg p-4 text-center">
            <div className="text-sm text-slate-400 mb-2">Total Transactions</div>
            <div className="text-2xl font-bold text-blue-400">
              {transactions.length}
            </div>
            <div className="text-xs text-slate-500 mt-1">All Real Transactions</div>
          </div>
          
          <div className="bg-black/40 rounded-lg p-4 text-center">
            <div className="text-sm text-slate-400 mb-2">Buy Transactions</div>
            <div className="text-2xl font-bold text-red-400">
              {transactions.filter(t => t.type === 'buy').length}
            </div>
            <div className="text-xs text-slate-500 mt-1">Real Purchases</div>
          </div>
          
          <div className="bg-black/40 rounded-lg p-4 text-center">
            <div className="text-sm text-slate-400 mb-2">Profit Transactions</div>
            <div className="text-2xl font-bold text-green-400">
              {transactions.filter(t => t.type === 'profit').length}
            </div>
            <div className="text-xs text-slate-500 mt-1">Real Profits</div>
          </div>
          
          <div className="bg-black/40 rounded-lg p-4 text-center">
            <div className="text-sm text-slate-400 mb-2">Stop Loss</div>
            <div className="text-2xl font-bold text-yellow-400">
              {transactions.filter(t => t.type === 'stop_loss').length}
            </div>
            <div className="text-xs text-slate-500 mt-1">Real Stop Losses</div>
          </div>
          
          <div className="bg-black/40 rounded-lg p-4 text-center">
            <div className="text-sm text-slate-400 mb-2">Sell Transactions</div>
            <div className="text-2xl font-bold text-orange-400">
              {transactions.filter(t => t.type === 'sell').length}
            </div>
            <div className="text-xs text-slate-500 mt-1">Real Sells</div>
          </div>
        </div>
      </div>

      {/* Transaction Filter Buttons */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <h2 className="text-2xl font-bold text-white mb-4 lg:mb-0">Transaction History</h2>
        
        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2">
          {['All', 'Buy Transaction', 'Profit Transaction', 'Stop Loss Transaction', 'Sell Transaction'].map((filter) => (
            <button
              key={filter}
              onClick={() => {
                setTransactionFilter(filter);
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                transactionFilter === filter
                  ? 'text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
              style={transactionFilter === filter ? { backgroundColor: '#22c55e' } : {}}
            >
              {filter} ({transactions.filter(t => {
                if (filter === 'All') return true;
                const filterMap = {
                  'Buy Transaction': 'buy',
                  'Profit Transaction': 'profit',
                  'Stop Loss Transaction': 'stop_loss',
                  'Sell Transaction': 'sell'
                };
                const transactionType = filterMap[filter];
                return transactionType && t.type === transactionType;
              }).length})
            </button>
          ))}
        </div>
      </div>

      {/* Items Per Page Selector */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-slate-400">Items per page:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-sm"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={30}>30</option>
            <option value={40}>40</option>
            <option value={50}>50</option>
            <option value={70}>70</option>
            <option value={100}>100</option>
          </select>
        </div>
        
        <span className="text-sm text-slate-400">
          Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredTransactions.length)} of {filteredTransactions.length} transactions
        </span>
      </div>

      {/* Real Trading Info Banner */}
      <div className="mb-6 p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
        <div className="flex items-center space-x-2">
          <span className="text-green-400 text-lg">✅</span>
          <div>
            <h4 className="text-green-400 font-semibold">Real Transaction History</h4>
            <p className="text-green-300 text-sm">
              This page shows all real transactions from your live trading activities. 
              All transactions are executed on the Solana blockchain and can be verified on Solscan.
            </p>
            <p className="text-green-200 text-xs mt-1">
              Red = Buy, Green = Profit, Yellow = Stop Loss, Orange = Sell
            </p>
          </div>
        </div>
      </div>

      {/* Transaction History */}
      {filteredTransactions.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-slate-400 text-lg">No transactions found for "{transactionFilter}" filter</p>
          <p className="text-slate-500 text-sm mt-2">Start live trading to see transaction history</p>
        </div>
      ) : (
        <div className="space-y-4">
          {paginatedTransactions.map((transaction, index) => (
            <div 
              key={transaction.id} 
              className={`card transition-all duration-300 hover:scale-[1.02] ${getTransactionColorClasses(transaction.color)}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="text-3xl">
                    {getTransactionIcon(transaction.type)}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">
                      {transaction.description}
                    </h3>
                    <div className="flex items-center space-x-4 mt-2">
                      <div className="text-sm">
                        <span className="text-slate-400">Token:</span>
                        <span className="ml-1 font-medium">{transaction.token}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-400">Amount:</span>
                        <span className="ml-1 font-medium">{transaction.amount} SOL</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-400">USD:</span>
                        <span className="ml-1 font-medium">
                          {loadingSolPrice ? 'Loading...' : formatUsdValue(transaction.amount)}
                        </span>
                      </div>
                    </div>
                    {transaction.price && (
                      <div className="text-sm mt-1">
                        <span className="text-slate-400">Price:</span>
                        <span className="ml-1 font-medium">${transaction.price}</span>
                      </div>
                    )}
                    {transaction.profitLoss !== undefined && (
                      <div className="text-sm mt-1">
                        <span className="text-slate-400">P&L:</span>
                        <span className={`ml-1 font-medium ${transaction.profitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {transaction.profitLoss >= 0 ? '+' : ''}{transaction.profitLoss.toFixed(2)}%
                        </span>
                      </div>
                    )}
                    {transaction.reason && (
                      <div className="text-sm mt-1">
                        <span className="text-slate-400">Reason:</span>
                        <span className="ml-1 font-medium">{transaction.reason}</span>
                      </div>
                    )}
                    {transaction.transactionId && (
                      <div className="text-sm mt-1">
                        <span className="text-slate-400">Transaction:</span>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-blue-400 font-mono text-xs">
                            {transaction.transactionId.length > 20 
                              ? `${transaction.transactionId.slice(0, 8)}...${transaction.transactionId.slice(-8)}`
                              : transaction.transactionId
                            }
                          </span>
                          <button
                            onClick={() => copyToClipboard(transaction.transactionId)}
                            className="text-slate-400 hover:text-blue-400 transition-colors"
                            title="Copy transaction ID"
                          >
                            <ClipboardDocumentIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openTransactionLink(transaction.transactionId)}
                            className="text-slate-400 hover:text-green-400 transition-colors"
                            title="View transaction on Solscan"
                          >
                            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                          </button>
                          {transaction.isMock && (
                            <span 
                              className="text-xs bg-yellow-900/50 text-yellow-400 px-1 py-0.5 rounded"
                              title="Mock transaction - no actual blockchain activity"
                            >
                              MOCK
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-sm text-slate-400 mb-1">Date & Time</div>
                  <div className="font-medium">
                    {formatTimeUTC1(transaction.timestamp)}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    UTC+1
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-2 rounded bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 rounded bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600"
            >
              Previous
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-2 rounded text-sm font-medium ${
                    currentPage === pageNum
                      ? 'text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                  style={currentPage === pageNum ? { backgroundColor: '#22c55e' } : {}}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 rounded bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 rounded bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600"
            >
              Last
            </button>
          </div>
        </div>
      )}

      {/* Transaction Legend */}
      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">Transaction Type Legend</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-4 h-4 rounded-full bg-red-500"></div>
            <span className="text-slate-300">Buy Transaction</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-4 h-4 rounded-full bg-green-500"></div>
            <span className="text-slate-300">Profit Transaction</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
            <span className="text-slate-300">Stop Loss Transaction</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-4 h-4 rounded-full bg-orange-500"></div>
            <span className="text-slate-300">Sell Transaction</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionsPage;
